import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { Box, Card, Typography } from "@mui/material";
import {
  CreatedAtOrganizer,
  Divider,
  Organizer,
  PeriodContainer,
  StyledBox,
  Title,
} from "./show-request-card.style";
import { useMyHrEmployee as useMyHrEmployeeDI } from "my-hr/hooks";
import { MyTimeRequestTypeInterface } from "my-time/components";
import { MY_TIME_DELETE_REQUEST } from "my-time/graphql";
import {
  OcCalendarDay,
  OcCardButtonBar,
  OcRequestsMessagesCard,
  OcStateChip,
} from "oc/components";
import { useOcSnackbar } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFullname } from "oc/utils";

interface MyTimeShowRequestCardProps {
  data: any; // Replace 'any' with the actual type if available
  messages: any; // Replace 'any' with the actual type if available
  loading: boolean;
  error?: any;
  employeeCode: string;
}

export const MyTimeShowRequestCard: React.FC<MyTimeShowRequestCardProps> = ({
  data,
  messages,
  loading,
  error,
  employeeCode,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const { data: approvedCode } = useMyHrEmployeeDI({
    employeeId: data?.approvedBy,
  });
  const { data: rejectedCode } = useMyHrEmployeeDI({
    employeeId: data?.rejectedBy,
  });

  const { data: employeeData } = useMyHrEmployeeDI({
    employeeId: employeeCode,
  });

  let locale = i18n.language.substr(0, 2).toLowerCase();

  const formatDateTime = (
    dateTime: string | Date | undefined,
    locale: string,
  ): string => {
    return moment(dateTime).locale(locale).format("YYYY. MM. DD. HH:mm");
  };

  let createdAt = formatDateTime(data?.createdAt, locale);
  let startTime = formatDateTime(data?.startTime, locale);
  let endTime = formatDateTime(data?.endTime, locale);

  const handleClose = () => {
    history.goBack();
  };

  const { setOpenMessage } = useOcSnackbar();

  const [deleteRequest] = useMutation(MY_TIME_DELETE_REQUEST, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_TIME_REQUEST_DELETE_SUCCEEDED"),
      });

      handleClose();
    },
    onError: () => {
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_REQUEST_DELETE_FAILED"),
      });
    },
  });

  const handleDelete = () => {
    deleteRequest({ variables: { _id: data?._id } });
  };

  return (
    <Card
      variant="outlined"
      style={{
        borderRadius: "4px",
        borderColor: error ? "#cc0033" : "white",
        overflow: "visible",
      }}
    >
      <StyledBox isMobile={isMobile}>
        <Title>{t("OC_REQUEST")}</Title>
        <Box height="24px" />
        <Divider />
        <Box height="24px" />
        <Organizer>
          <Typography variant="h6">{getFullname(employeeData)}</Typography>
          <CreatedAtOrganizer>
            <Box fontSize="9px" paddingTop="1px" style={{ color: "#919699" }}>
              {createdAt}
            </Box>
            <Box height="2px" />
            {data?.state === "approved" && (
              <Box fontSize="9px" paddingTop="1px" style={{ color: "#919699" }}>
                {getFullname(approvedCode)}
              </Box>
            )}
            {data?.state === "rejected" && (
              <Box fontSize="9px" paddingTop="1px" style={{ color: "#919699" }}>
                {getFullname(rejectedCode)}
              </Box>
            )}
            <Box height="2px" />
            <OcStateChip
              label={!loading && t(`MY_LEAVE_${data?.state.toUpperCase()}`)}
              state={data?.state}
              loading={loading}
              variant="body1"
            />
          </CreatedAtOrganizer>
        </Organizer>
        <Box height="24px" />
        <Box
          display="flex"
          alignItems={isMobile ? "center" : "unset"}
          justifyContent={isMobile ? "center" : "unset"}
        >
          <OcCalendarDay date={data?.hours} loading={loading} hours={true} unPaidDate={false} />
          <Box width="16px" />
          <PeriodContainer isMobile={isMobile}>
            <Typography>{t("OC_PERIOD")}</Typography>
            <Box>
              {startTime} - {endTime}
            </Box>
          </PeriodContainer>
        </Box>
        <Box height="24px" />
        <MyTimeRequestTypeInterface
          data={data}
          myTimeType={data?.myTimeType}
          timeDataTypeName={data?.timeDataTypeName}
          unPaidDate={false}
        />
        <Box height="24px" />
        {!loading && (
          <OcRequestsMessagesCard
            messages={messages}
            module="MyTime"
            loading={loading}
            data={data}
            typeGroup={data?.typeGroup || ""}
            type={data?.type || ""}
            selectedId={data?._id || ""}
            typeGroupId={data?.typeGroupId || ""}
            disabled={false}
            refetch={() => { }}
            isMobile={isMobile}
            role=""
          />
        )}
        <OcCardButtonBar
          leftButtonText={t("OC_REMOVE_REQUEST")}
          leftButtonColor={data?.state === "requested" ? "primary" : undefined}
          leftButtonDisabled={loading}
          handleLeftButton={handleDelete}
          leftButtonId="request-page-delete-button"
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          rightButtonDisabled={loading}
          handleRightButton={handleClose}
          rightButtonId="request-page-cancel-button"
        />
      </StyledBox>
    </Card>
  );
};

export default MyTimeShowRequestCard;
